'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── AL-FATIHA SESSION OPENING ────────────────────────────────────────────────

export function AlFatihaSesssionOpening({ onEnter }: { onEnter: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem('haal_fatiha_shown');
    if (lastShown !== today) {
      setShown(true);
    } else {
      setDismissed(true);
    }
  }, []);

  const handleEnter = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_fatiha_shown', new Date().toDateString());
    }
    setDismissed(true);
    onEnter();
  };

  if (dismissed || !shown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(6,13,18,0.97)', backdropFilter: 'blur(20px)' }}
    >
      <div className="max-w-lg w-full mx-6 text-center">
        {/* Arabic */}
        <div className="mb-8">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-6" style={{ color: 'rgba(201,168,76,0.4)' }}>
            ◈ Before You Enter
          </p>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: 'rgba(201,168,76,0.85)', direction: 'rtl', lineHeight: 2, marginBottom: 16 }}>
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: 'rgba(201,168,76,0.65)', direction: 'rtl', lineHeight: 2 }}>
            اهْدِنَا الصِّرَاطَ الْمُسْتَقِيمَ
          </div>
        </div>

        {/* Instruction */}
        <div className="rounded-xl p-6 mb-8" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>Al-Fatiha · The Opening</p>
          <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.85 }}>
            Begin with Al-Fatiha. Then enter.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, lineHeight: 1.7, marginTop: 8 }}>
            The way every salah begins with Al-Fatiha, every garden session begins with the recognition that guidance (Ihdini) comes from Allah (Rabb Al-Alameen) and that the caretaker asks for it (Eiyyaka nastainu) before they proceed.
          </p>
        </div>

        {/* Quran 15:85-87 reference */}
        <div className="rounded-lg p-4 mb-8" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)' }}>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.7 }}>
            "And certainly, We have given you seven of the oft-repeated and the Quran Great."
          </p>
          <p className="font-mono text-[8px] mt-2" style={{ color: 'rgba(45,122,71,0.5)' }}>Quran 15:87</p>
        </div>

        <button
          onClick={handleEnter}
          className="font-mono text-[10px] uppercase tracking-widest px-8 py-3 rounded-xl transition-all"
          style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}
        >
          Enter My Garden ◈
        </button>
        <p className="font-mono text-[8px] mt-3" style={{ color: 'rgba(212,197,169,0.25)' }}>
          Shown once per session
        </p>
      </div>
    </div>
  );
}

// ─── ALHAMDULILLAH FOUNDATIONAL COUNTER ──────────────────────────────────────

export function AlhamdulillahFoundationalCounter({ count, role, lineageColor }: {
  count: number;
  role?: string;
  lineageColor?: string;
}) {
  const [showInfo, setShowInfo] = useState(false);
  const color = lineageColor || '#c9a84c';

  return (
    <div className="rounded-xl p-5" style={{ background: `${color}06`, border: `1px solid ${color}18` }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: `${color}80` }}>Alhamdulillah</p>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color, marginBottom: 4 }}>الْحَمْدُ لِلَّهِ</p>
        </div>
        <button onClick={() => setShowInfo(!showInfo)} style={{ color: 'rgba(212,197,169,0.3)', fontSize: 14 }}>ⓘ</button>
      </div>

      {/* Ground state indicator */}
      <div className="rounded-lg p-3 mb-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.4)' }}>Ground State</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>
          Alhamdulillah is already the state of everything. This counter measures your awareness of what already is.
        </p>
      </div>

      {/* Count display */}
      <div className="flex items-end gap-2 mb-2">
        <div className="font-mono text-4xl font-black" style={{ color }}>{count}</div>
        <div className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.4)' }}>
          recognitions of the ground state
        </div>
      </div>

      {showInfo && (
        <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(6,13,18,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6 }}>
            The counter does not start at zero — it starts at Alhamdulillah. The mural demonstrates "the only state of existence is rendering due praise." The counter asks: how many times have you consciously recognized that creation was already praising, and said Alhamdulillah alongside it?
          </p>
          {role && (
            <p className="font-mono text-[8px] mt-2" style={{ color: `${color}60` }}>
              Role-weighted: {role} path
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── YAQEEN MODULE TILE TRACKER ───────────────────────────────────────────────

export type YaqeenModuleState = {
  ilm: boolean;
  ayn: boolean;
  haqq: boolean;
  batinDepth: number; // 1-7
};

export function YaqeenModuleTile({
  moduleId,
  moduleLabel,
  moduleColor = '#2d8a87',
  href,
}: {
  moduleId: string;
  moduleLabel: string;
  moduleColor?: string;
  href?: string;
}) {
  const [state, setState] = useState<YaqeenModuleState>({ ilm: false, ayn: false, haqq: false, batinDepth: 0 });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(`haal_yaqeen_tile_${moduleId}`);
    if (saved) {
      try { setState(JSON.parse(saved)); } catch {}
    }
  }, [moduleId]);

  const saveState = (s: YaqeenModuleState) => {
    setState(s);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`haal_yaqeen_tile_${moduleId}`, JSON.stringify(s));
    }
  };

  const toggle = (level: 'ilm' | 'ayn' | 'haqq') => {
    saveState({ ...state, [level]: !state[level] });
  };

  const setDepth = (depth: number) => {
    saveState({ ...state, batinDepth: depth });
  };

  const fullYaqeen = state.ilm && state.ayn && state.haqq;
  const yaqeenScore = (state.ilm ? 1 : 0) + (state.ayn ? 3 : 0) + (state.haqq ? 7 : 0);

  const LEVELS = [
    { id: 'ilm' as const, label: 'Ilm', color: '#2d7a47', desc: 'Knowledge — you read about it' },
    { id: 'ayn' as const, label: 'Ayn', color: '#2d8a87', desc: 'Perception — you saw evidence' },
    { id: 'haqq' as const, label: 'Haqq', color: '#c9a84c', desc: 'Reality — you experienced it' },
  ];

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        border: `1px solid ${fullYaqeen ? 'rgba(201,168,76,0.4)' : `${moduleColor}20`}`,
        background: fullYaqeen ? 'rgba(201,168,76,0.04)' : `${moduleColor}04`,
        boxShadow: fullYaqeen ? `0 0 20px rgba(201,168,76,0.08)` : 'none',
      }}
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {/* Yaqeen state indicators */}
          <div className="flex gap-1">
            {LEVELS.map(l => (
              <div
                key={l.id}
                className="w-2 h-2 rounded-full"
                style={{ background: state[l.id] ? l.color : 'rgba(255,255,255,0.08)' }}
                title={l.label}
              />
            ))}
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest font-black" style={{ color: '#d4c5a9' }}>{moduleLabel}</span>
          {fullYaqeen && <span style={{ color: '#c9a84c', fontSize: 12 }}>✦</span>}
        </div>
        <div className="flex items-center gap-3">
          {state.batinDepth > 0 && (
            <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${moduleColor}12`, color: moduleColor }}>
              B{state.batinDepth}
            </span>
          )}
          <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: `${moduleColor}12` }}>
          {/* Yaqeen level toggles */}
          <div className="mt-3 mb-4">
            <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.35)' }}>Yaqeen Level (how you came to know)</p>
            <div className="flex gap-2">
              {LEVELS.map(l => (
                <button
                  key={l.id}
                  onClick={() => toggle(l.id)}
                  className="flex-1 rounded-lg py-2 transition-all"
                  style={{
                    background: state[l.id] ? `${l.color}15` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${state[l.id] ? `${l.color}40` : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ background: state[l.id] ? l.color : 'rgba(255,255,255,0.1)' }} />
                  <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: state[l.id] ? l.color : 'rgba(212,197,169,0.3)' }}>{l.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Batin Depth (orthogonal) */}
          <div className="mb-3">
            <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.35)' }}>Batin Depth (how deep you've gone)</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6, 7].map(d => (
                <button
                  key={d}
                  onClick={() => setDepth(d === state.batinDepth ? 0 : d)}
                  className="flex-1 h-6 rounded transition-all font-mono text-[8px]"
                  style={{
                    background: state.batinDepth >= d ? `${moduleColor}20` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${state.batinDepth >= d ? `${moduleColor}35` : 'rgba(255,255,255,0.05)'}`,
                    color: state.batinDepth >= d ? moduleColor : 'rgba(212,197,169,0.2)',
                  }}
                >
                  {d}
                </button>
              ))}
            </div>
            <p className="font-mono text-[7px] mt-1" style={{ color: 'rgba(212,197,169,0.25)' }}>
              Yaqeen and Batin Depth are orthogonal — they measure different things
            </p>
          </div>

          {/* Score */}
          <div className="flex items-center justify-between">
            <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Yaqeen score: {yaqeenScore} / 11
            </span>
            {href && (
              <Link href={href} className="font-mono text-[8px] uppercase tracking-widest px-2 py-1 rounded transition-all" style={{ border: '1px solid rgba(201,168,76,0.15)', color: 'rgba(201,168,76,0.6)' }}>
                Open Module →
              </Link>
            )}
          </div>

          {state.haqq && (
            <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: '#c9a84c' }}>Haqq Yaqeen — Go See For Yourself</p>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11 }}>
                You've marked this module at Haqq Yaqeen. The platform's graduation ceremony happens outside. Close the screen. Go to the waterfront.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── "REMIND IF BENEFITS" SELF-CHECK ─────────────────────────────────────────

export function RemindIfBenefitsCheck({ sessionCount }: { sessionCount: number }) {
  const [dismissed, setDismissed] = useState(false);
  const [response, setResponse] = useState<'yes' | 'no' | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_remind_check_dismissed');
    if (saved) setDismissed(true);
  }, []);

  // Show after 7 sessions with no depth progression
  if (sessionCount < 7 || dismissed) return null;

  const handleResponse = (r: 'yes' | 'no') => {
    setResponse(r);
    if (r === 'no') {
      setTimeout(() => {
        setDismissed(true);
        if (typeof window !== 'undefined') {
          localStorage.setItem('haal_remind_check_dismissed', 'true');
        }
      }, 3000);
    } else {
      setDismissed(true);
      if (typeof window !== 'undefined') {
        localStorage.setItem('haal_remind_check_dismissed', 'true');
      }
    }
  };

  if (response === 'no') {
    return (
      <div className="rounded-xl p-5" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)' }}>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>◎ Go Tend Your Real Garden</p>
        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7 }}>
          "So remind if benefits the reminder." — Quran 87:9
        </p>
        <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, marginTop: 8 }}>
          The platform will be here when you return. Go outside. The Haqq Yaqeen is yours to receive, not ours to give.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ A Question for You</p>
      <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 14, lineHeight: 1.8, marginBottom: 4 }}>
        "So remind if benefits the reminder."
      </p>
      <p className="font-mono text-[8px] mb-4" style={{ color: 'rgba(212,197,169,0.35)' }}>Quran 87:9</p>
      <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
        You've been returning to the garden for {sessionCount} sessions. Is this reminder still benefiting you? Is your engagement deepening recognition — or has it become a routine?
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => handleResponse('yes')}
          className="flex-1 font-mono text-[9px] uppercase tracking-widest py-2 rounded transition-all"
          style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', color: '#2d7a47' }}
        >
          Yes, it benefits
        </button>
        <button
          onClick={() => handleResponse('no')}
          className="flex-1 font-mono text-[9px] uppercase tracking-widest py-2 rounded transition-all"
          style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}
        >
          Go outside instead
        </button>
      </div>
    </div>
  );
}

// ─── ISNAD CHAIN DISPLAY ─────────────────────────────────────────────────────

export interface IsnadLink {
  author: string;
  timestamp: number;
  vantagePoint: string;
  city?: string;
  verified: boolean;
  verifiedBy?: string;
  note?: string;
}

export function IsnadChainDisplay({ chain, observationTitle }: {
  chain: IsnadLink[];
  observationTitle: string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(201,168,76,0.12)' }}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        style={{ background: 'rgba(201,168,76,0.03)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <p className="font-mono text-[8px] uppercase tracking-widest mb-0.5" style={{ color: 'rgba(201,168,76,0.5)' }}>Isnad — Chain of Transmission</p>
          <p className="font-mono text-[10px]" style={{ color: '#d4c5a9' }}>{observationTitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.08)', color: '#c9a84c' }}>
            {chain.length} link{chain.length !== 1 ? 's' : ''}
          </span>
          <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t" style={{ borderColor: 'rgba(201,168,76,0.08)' }}>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, marginBottom: 12, lineHeight: 1.6 }}>
            Isnad for ecological recognition — the same epistemological discipline that preserves Hadith literature applied to the community's observation catalog. Who saw it, when, from what vantage, and who verified.
          </p>
          <div className="space-y-3">
            {chain.map((link, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center font-mono text-[9px] font-black flex-shrink-0" style={{ background: link.verified ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.06)', color: link.verified ? '#2d7a47' : 'rgba(212,197,169,0.4)' }}>
                    {i + 1}
                  </div>
                  {i < chain.length - 1 && <div className="w-px flex-1 mt-1" style={{ background: 'rgba(255,255,255,0.06)', minHeight: 16 }} />}
                </div>
                <div className="flex-1 pb-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[9px] font-black" style={{ color: '#d4c5a9' }}>{link.author}</span>
                    {link.city && <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.4)' }}>{link.city}</span>}
                    {link.verified && (
                      <span className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.1)', color: '#2d7a47' }}>
                        ✓ Verified{link.verifiedBy ? ` by ${link.verifiedBy}` : ''}
                      </span>
                    )}
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>
                    {new Date(link.timestamp).toLocaleDateString()} · {link.vantagePoint}
                  </p>
                  {link.note && (
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, fontStyle: 'italic', marginTop: 2 }}>"{link.note}"</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GO SEE FOR YOURSELF PROMPT ───────────────────────────────────────────────

export function GoSeeForYourselfPrompt({ context }: { context?: string }) {
  return (
    <div className="rounded-xl p-5 mt-4" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderLeft: '3px solid rgba(201,168,76,0.5)' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: '#c9a84c' }}>◈ Haqq Yaqeen — Terminal Instruction</p>
      <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 14, fontStyle: 'italic', marginBottom: 8 }}>
        "Go see for yourself."
      </p>
      <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.7 }}>
        {context || 'The platform provides Ilm and Ayn Yaqeen. Only the world provides Haqq. Close the screen. Go to the waterfront, to the ward, to the forest, to the vacant lot. Haqq Yaqeen is the platform\'s graduation ceremony, and it happens outside.'}
      </p>
    </div>
  );
}

// ─── NAMES REFERENCE FITRA NOTE ──────────────────────────────────────────────

export function NamesReferenceFitraNote({ nameName }: { nameName: string }) {
  return (
    <div className="rounded-lg p-4" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
      <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>A Reminder, Not an Introduction</p>
      <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7 }}>
        You already know <span style={{ color: '#c9a84c' }}>{nameName}</span>. It was placed in your Fitra — your original design. This page is a reminder, not an introduction. The Names are not new information. They are being recalled. The caretaker already knows them, in the Fitra sense. The Names Reference helps you recall Names you were designed to recognize.
      </p>
    </div>
  );
}

// ─── SUBSURFACE LAYERS VISUALIZATION ─────────────────────────────────────────

export function SubsurfaceLayersVisualization() {
  const [activeLayer, setActiveLayer] = useState<'fitra' | 'marifah' | 'keys' | null>(null);

  const layers = [
    {
      id: 'keys' as const,
      label: 'Keys',
      sublabel: 'Soil',
      desc: 'The informational framework — enriched by curriculum engagement. The five Keys as soil minerals. Ilm Yaqeen prepares the soil.',
      color: '#D4A82A',
      depth: 1,
      modules: ['Keys to Kulli Haal', 'SL Glossary', 'Names Reference'],
    },
    {
      id: 'marifah' as const,
      label: "Ma'rifah",
      sublabel: 'Roots',
      desc: 'Experiential knowing — growing deeper with Haqq Yaqeen engagement. The roots are invisible but they hold everything up. They grow through doing, not studying.',
      color: '#2d7a47',
      depth: 2,
      modules: ['IRL Lens', 'Observation Chain', 'Ward Walks'],
    },
    {
      id: 'fitra' as const,
      label: 'Fitra',
      sublabel: 'Bedrock',
      desc: 'The original design — indestructible, always present. The capacity for recognition was placed in you before you arrived. You don\'t build Fitra. You return to it.',
      color: '#5a5a8a',
      depth: 3,
      modules: ['Guided Intro', 'Lineage', 'Original Design'],
    },
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(45,122,71,0.15)' }}>
      <div className="p-4 border-b" style={{ borderColor: 'rgba(45,122,71,0.1)', background: 'rgba(45,122,71,0.03)' }}>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.6)' }}>Three Subsurface Layers</p>
        <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11 }}>Fitra bedrock / Ma'rifah roots / Keys soil</p>
      </div>
      <div className="p-4">
        {/* Visual representation */}
        <div className="relative mb-4">
          {layers.map((layer, i) => (
            <div
              key={layer.id}
              onClick={() => setActiveLayer(activeLayer === layer.id ? null : layer.id)}
              className="cursor-pointer transition-all mb-1"
              style={{
                marginLeft: `${i * 8}px`,
                marginRight: `${i * 8}px`,
              }}
            >
              <div
                className="rounded-lg p-3 flex items-center justify-between"
                style={{
                  background: activeLayer === layer.id ? `${layer.color}12` : `${layer.color}06`,
                  border: `1px solid ${activeLayer === layer.id ? `${layer.color}35` : `${layer.color}15`}`,
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: layer.depth }).map((_, j) => (
                      <div key={j} className="w-1 h-3 rounded-full" style={{ background: layer.color }} />
                    ))}
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-widest font-black" style={{ color: layer.color }}>{layer.label}</span>
                  <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${layer.color}12`, color: layer.color }}>{layer.sublabel}</span>
                </div>
                <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>{activeLayer === layer.id ? '▲' : '▼'}</span>
              </div>

              {activeLayer === layer.id && (
                <div className="rounded-b-lg p-3 -mt-1" style={{ background: `${layer.color}06`, border: `1px solid ${layer.color}15`, borderTop: 'none' }}>
                  <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>{layer.desc}</p>
                  <div className="flex gap-1 flex-wrap">
                    {layer.modules.map(m => (
                      <span key={m} className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${layer.color}10`, color: layer.color, border: `1px solid ${layer.color}20` }}>{m}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 10, lineHeight: 1.5 }}>
          Ma'rifah grows through Haqq Yaqeen engagement. The roots are invisible but they hold everything up.
        </p>
      </div>
    </div>
  );
}
